import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { tl } from "@/lib/i18nFormat";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { TimerReset, LogIn, LogOut, Coffee, Play, AlertTriangle, CheckCircle2, FileWarning, Send } from "lucide-react";

const fmtMin = (m: number) => `${Math.floor(m / 60)}h ${m % 60}m`;
const fmtH = (m: number) => `${((m || 0) / 60).toFixed(1)}h`;
const fmtVar = (m: number) => `${m > 0 ? "+" : ""}${((m || 0) / 60).toFixed(1)}h`;
const localFromMinute = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

export default function MyTime() {
  const {
    t
  } = useI18n();

  const [profile, setProfile] = useState<any>(null);
  const [storeId, setStoreId] = useState<string>("");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadToday = useCallback((sid: string) => {
    api.get(`/time/me/today?store_id=${sid}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api.get("/scheduling/me/profile").then((r) => {
      const p = r.data.profile; setProfile(p);
      if (p?.primary_store_id) { setStoreId(p.primary_store_id); loadToday(p.primary_store_id); }
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, [loadToday]);

  const punch = async (type: string) => {
    setBusy(true);
    try { const r = await api.post("/time/me/punch", { type, store_id: storeId }); toast.success(`${type.replace("_", " ")} · ${r.data.at}`); loadToday(storeId); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (loading) return <LoadingBlock />;
  if (!profile) {
    return (
      <div>
        <PageHeader title="Time Clock" testId="mytime-header" />
        <EmptyState icon={<TimerReset size={28} />} title="No workforce profile yet" description="Your manager will add you to the roster so you can clock in." />
      </div>
    );
  }

  const state = data?.state || "OUT";
  const stateLabel: Record<string, string> = { OUT: "Clocked out", IN: "On the clock", BREAK: "On break" };
  const stateTone: Record<string, string> = { OUT: "inactive", IN: "good", BREAK: "warn" };

  return (
    (<div>
      <PageHeader title="Time Clock" subtitle={data?.store_name ? `${tl("Store")} ${data.store_number} · ${data.store_name}` : ""} testId="mytime-header" />
      <Card className="mx-auto max-w-md p-6 text-center" data-testid="mytime-clock">
        <div className="font-display text-5xl font-bold tabular-nums" data-testid="mytime-localtime">{data?.store_local_time}</div>
        <div className="mt-2"><StatusPill status={stateTone[state]} testId="mytime-state">{stateLabel[state]}</StatusPill></div>
        {data?.shift ? (
          <div className="mt-2 text-sm text-muted-foreground">{t("Scheduled today")}{" "}{data.shift.start}–{data.shift.end}</div>
        ) : <div className="mt-2 text-sm text-muted-foreground">{t("No shift scheduled today")}</div>}
        <div className="mt-1 text-xs text-muted-foreground">{t("Worked so far:")}{" "}{fmtMin(data?.worked_minutes || 0)}{data?.break_minutes ? ` · ${tl("break")} ${data.break_minutes}m` : ""}</div>

        <div className="mt-6 flex flex-col gap-2">
          {state === "OUT" && <Button size="lg" disabled={busy} onClick={() => punch("clock_in")} data-testid="mytime-clock-in"><LogIn size={18} className="mr-2" />{t("Clock in")}</Button>}
          {state === "IN" && (
            <>
              <Button size="lg" variant="outline" disabled={busy} onClick={() => punch("break_start")} data-testid="mytime-break-start"><Coffee size={18} className="mr-2" />{t("Start break")}</Button>
              <Button size="lg" disabled={busy} onClick={() => punch("clock_out")} data-testid="mytime-clock-out"><LogOut size={18} className="mr-2" />{t("Clock out")}</Button>
            </>
          )}
          {state === "BREAK" && <Button size="lg" disabled={busy} onClick={() => punch("break_end")} data-testid="mytime-break-end"><Play size={18} className="mr-2" />{t("End break")}</Button>}
        </div>
      </Card>
      {(data?.punches || []).length > 0 && (
        <div className="mx-auto mt-4 max-w-md">
          <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("Today's punches")}</div>
          <div className="flex flex-wrap gap-1" data-testid="mytime-punches">
            {data.punches.map((p: any) => <span key={p.id} className="rounded bg-secondary px-2 py-0.5 text-[11px]">{p.type.replace("_", " ")} {localFromMinute(p.local_minute)}</span>)}
          </div>
        </div>
      )}
      <MyTimecardsPanel />
    </div>)
  );
}

const STATUS_TONE: Record<string, string> = { present: "good", late: "warn", left_early: "warn", absent: "bad", incomplete: "warn", unscheduled: "info" };

const MyTimecardsPanel: React.FC = () => {
  const { t } = useI18n();
  const [cards, setCards] = useState<any[] | null>(null);
  const [corrections, setCorrections] = useState<any[]>([]);
  const [dispute, setDispute] = useState<{ tc: any; action: "acknowledge" | "dispute" } | null>(null);
  const [note, setNote] = useState("");
  const [correct, setCorrect] = useState<any | null>(null);
  const [ctype, setCtype] = useState("missing_punch");
  const [cin, setCin] = useState("");
  const [cout, setCout] = useState("");
  const [creason, setCreason] = useState("");
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState<any>(null);
  const [attest, setAttest] = useState<any | null>(null);
  const [att, setAtt] = useState({ accuracy: false, meal: false, rest: false, off: false, offNote: "" });

  const load = useCallback(() => {
    api.get("/time/me/timecards?limit=14").then((r) => setCards(r.data.timecards)).catch(() => setCards([]));
    api.get("/time/me/corrections").then((r) => setCorrections(r.data.corrections)).catch(() => setCorrections([]));
    api.get("/time/me/attestation-policy").then((r) => setPolicy(r.data)).catch(() => setPolicy(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSubmit = (tc: any) => {
    if (policy?.enabled) {
      setAtt({ accuracy: false, meal: false, rest: false, off: false, offNote: "" });
      setAttest(tc);
    } else {
      doSubmit(tc, null);
    }
  };
  const doSubmit = async (tc: any, attestation: any) => {
    setBusy(true);
    try {
      await api.post(`/time/me/timecards/${tc.id}/submit`, attestation ? { attestation } : {});
      toast.success("Timecard submitted for approval");
      setAttest(null); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const submitAttested = () => {
    if (!attest) return;
    if (!att.accuracy) { toast.error(t("attest.required")); return; }
    if (att.off && !att.offNote.trim()) { toast.error(t("attest.off_clock_note")); return; }
    doSubmit(attest, {
      accuracy_confirmed: att.accuracy, meal_break_taken: att.meal, rest_breaks_taken: att.rest,
      worked_off_clock: att.off, off_clock_note: att.off ? att.offNote : null,
    });
  };

  const submitDispute = async () => {
    if (!dispute) return;
    if (dispute.action === "dispute" && !note.trim()) { toast.error("A note is required to dispute"); return; }
    setBusy(true);
    try {
      await api.post(`/time/me/timecards/${dispute.tc.id}/dispute`, { action: dispute.action, note: note || null });
      toast.success(dispute.action === "dispute" ? tl("Dispute submitted") : tl("Timecard acknowledged"));
      setDispute(null); setNote(""); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submitCorrection = async () => {
    if (!correct) return;
    if (!creason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post("/time/corrections", { timecard_id: correct.id, type: ctype, clock_in: cin || null, clock_out: cout || null, reason: creason });
      toast.success("Correction requested — your manager will review it");
      setCorrect(null); setCin(""); setCout(""); setCreason(""); setCtype("missing_punch"); load();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (cards === null) return null;

  const corrByTc = (tcId: string) => corrections.filter((c) => c.timecard_id === tcId);
  const lastDisposition = (tc: any) => (tc.disputes && tc.disputes.length ? tc.disputes[tc.disputes.length - 1] : null);

  return (
    (<div className="mx-auto mt-8 max-w-md" data-testid="mytime-timecards">
      <div className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("My timecards")}</div>
      {cards.length === 0 ? (
        <EmptyState icon={<TimerReset size={22} />} title="No timecards yet" description="Once you clock in, your daily timecard appears here to review." />
      ) : (
        <div className="space-y-2">
          {cards.map((tc) => {
            const disp = lastDisposition(tc);
            const pending = corrByTc(tc.id).filter((c) => c.status === "pending");
            return (
              (<Card key={tc.id} className="p-3 text-sm" data-testid={`mytime-tc-${tc.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium">{tc.business_date}</div>
                  <StatusPill status={tc.approval?.status === "approved" ? "good" : STATUS_TONE[tc.status] || "info"}>{tc.approval?.status === "approved" ? t("approved") : tc.status}</StatusPill>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">{tc.clock_in || "—"}→ {tc.clock_out || "—"}· {fmtH(tc.worked_minutes)}{" "}{t("worked · var")}{" "}{fmtVar(tc.variance_minutes)}</div>
                {tc.unresolved_exceptions?.length > 0 && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-status-warn"><AlertTriangle size={12} />{tc.unresolved_exceptions.map((e: any) => e.code.replace("_", " ")).join(", ")}</div>
                )}
                {disp && <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`mytime-tc-disp-${tc.id}`}>{t("You")}{" "}{disp.action === "dispute" ? t("disputed") : t("acknowledged")}{" "}{t("this timecard")}{disp.note ? ` · "${disp.note}"` : ""}</div>}
                {pending.length > 0 && <div className="mt-1 text-[11px] text-status-warn">{t("Correction requested — pending manager review")}</div>}
                <div className="mt-2 flex flex-wrap gap-2">
                  {["open", "reopened"].includes(tc.state) && !tc.open && (
                    <Button size="sm" onClick={() => openSubmit(tc)} data-testid={`mytime-submit-${tc.id}`}><Send size={13} className="mr-1" />{t("attest.submit_action")}</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setDispute({ tc, action: "acknowledge" }); setNote(""); }} data-testid={`mytime-ack-${tc.id}`}><CheckCircle2 size={13} className="mr-1" />{t("Acknowledge")}</Button>
                  <Button size="sm" variant="outline" onClick={() => { setDispute({ tc, action: "dispute" }); setNote(""); }} data-testid={`mytime-dispute-${tc.id}`}><AlertTriangle size={13} className="mr-1" />{t("Dispute")}</Button>
                  <Button size="sm" variant="outline" onClick={() => { setCorrect(tc); }} data-testid={`mytime-correction-open-${tc.id}`}><FileWarning size={13} className="mr-1" />{t("Request correction")}</Button>
                </div>
              </Card>)
            );
          })}
        </div>
      )}
      <Dialog open={!!dispute} onOpenChange={(o) => { if (!o) setDispute(null); }}>
        <DialogContent data-testid="mytime-dispute-dialog">
          <DialogHeader><DialogTitle>{dispute?.action === "dispute" ? t("Dispute timecard") : t("Acknowledge timecard")}</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">{dispute?.tc.business_date}· {fmtH(dispute?.tc.worked_minutes || 0)}{" "}{t("worked")}</div>
          <div className="mt-2">
            <Label className="text-xs">{dispute?.action === "dispute" ? t("What is wrong? (required)") : t("Note (optional)")}</Label>
            <Textarea className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} data-testid="mytime-dispute-note" />
          </div>
          <DialogFooter>
            <Button disabled={busy} onClick={submitDispute} data-testid="mytime-dispute-submit">{dispute?.action === "dispute" ? t("Submit dispute") : t("Acknowledge")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!correct} onOpenChange={(o) => { if (!o) setCorrect(null); }}>
        <DialogContent data-testid="mytime-correction-dialog">
          <DialogHeader><DialogTitle>{t("Request a correction")}</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">{correct?.business_date}{" "}{t("— your manager reviews and approves before anything changes.")}</div>
          <div className="mt-2 space-y-3">
            <div>
              <Label className="text-xs">{t("Type")}</Label>
              <Select value={ctype} onValueChange={setCtype}>
                <SelectTrigger className="mt-1" data-testid="mytime-corr-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="missing_punch">{t("Missing punch")}</SelectItem>
                  <SelectItem value="incorrect_punch">{t("Incorrect punch")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">{t("Clock in (HH:MM)")}</Label><Input className="mt-1" placeholder="09:00" value={cin} onChange={(e) => setCin(e.target.value)} data-testid="mytime-corr-in" /></div>
              <div><Label className="text-xs">{t("Clock out (HH:MM)")}</Label><Input className="mt-1" placeholder="17:00" value={cout} onChange={(e) => setCout(e.target.value)} data-testid="mytime-corr-out" /></div>
            </div>
            <div><Label className="text-xs">{t("Reason (required)")}</Label><Textarea className="mt-1" value={creason} onChange={(e) => setCreason(e.target.value)} data-testid="mytime-corr-reason" /></div>
          </div>
          <DialogFooter>
            <Button disabled={busy} onClick={submitCorrection} data-testid="mytime-corr-submit">{t("Submit request")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!attest} onOpenChange={(o) => { if (!o) setAttest(null); }}>
        <DialogContent data-testid="mytime-attest-dialog">
          <DialogHeader><DialogTitle>{t("attest.title")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("attest.subtitle")}</p>
          {policy?.policy_text && <p className="rounded-md bg-secondary/60 p-2 text-xs text-muted-foreground">{policy.policy_text}</p>}
          <div className="mt-1 space-y-3">
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={att.accuracy} onCheckedChange={(v) => setAtt((s) => ({ ...s, accuracy: !!v }))} data-testid="attest-accuracy" />
              <span>{t("attest.accuracy")}</span>
            </label>
            {policy?.require_meal_rest && (
              <>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={att.meal} onCheckedChange={(v) => setAtt((s) => ({ ...s, meal: !!v }))} data-testid="attest-meal" />
                  <span>{t("attest.meal")}</span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={att.rest} onCheckedChange={(v) => setAtt((s) => ({ ...s, rest: !!v }))} data-testid="attest-rest" />
                  <span>{t("attest.rest")}</span>
                </label>
              </>
            )}
            {policy?.off_clock_disclosure && (
              <div className="rounded-md border border-border p-2">
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox checked={att.off} onCheckedChange={(v) => setAtt((s) => ({ ...s, off: !!v }))} data-testid="attest-offclock" />
                  <span>{t("attest.off_clock_yes")}</span>
                </label>
                {att.off && (
                  <div className="mt-2">
                    <Textarea placeholder={t("attest.off_clock_note")} value={att.offNote} onChange={(e) => setAtt((s) => ({ ...s, offNote: e.target.value }))} data-testid="attest-offclock-note" />
                    <p className="mt-1 text-[11px] text-status-warn">{t("attest.off_clock_hint")}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button disabled={busy || !att.accuracy} onClick={submitAttested} data-testid="attest-submit">{t("attest.submit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>)
  );
};
